/**
 * Referral code capture and claim utilities.
 *
 * Referral attribution is retained in the first-party cookie created by the
 * marketing site. After login, the dashboard claims it via the API and clears
 * only the referral code, leaving the visitor's cookie preference intact.
 */

const REFERRAL_COOKIE_NAME = 'miles_ref';
const REFERRAL_STORAGE_PERMISSION_COOKIE_NAME = 'miles_ref_storage';
const REFERRAL_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;
const VALID_REFERRAL = /^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/;

function getCookie(name: string): string | null {
  const match = document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${name}=`));
  if (!match) return null;

  try {
    return decodeURIComponent(match.slice(name.length + 1));
  } catch {
    return null;
  }
}

function normalizeReferralCode(value: string | null): string | null {
  const normalized = value?.trim().toLowerCase() ?? '';
  return VALID_REFERRAL.test(normalized) ? normalized : null;
}

/**
 * Read the miles_ref cookie set by the marketing site (bymiles.ai).
 */
function getReferralCookie(): string | null {
  return normalizeReferralCode(getCookie(REFERRAL_COOKIE_NAME));
}

function setReferralCookie(referralCode: string): void {
  const hostname = window.location.hostname.toLowerCase();
  const domain =
    hostname === 'bymiles.ai' || hostname.endsWith('.bymiles.ai')
      ? '; domain=.bymiles.ai'
      : '';
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `${REFERRAL_COOKIE_NAME}=${encodeURIComponent(referralCode)}${domain}; path=/; max-age=${REFERRAL_COOKIE_MAX_AGE_SECONDS}; samesite=lax${secure}`;
}

/**
 * Clear the cross-subdomain cookie after the authenticated claim succeeds.
 */
function clearReferralCookie(): void {
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `${REFERRAL_COOKIE_NAME}=; domain=.bymiles.ai; path=/; max-age=0; samesite=lax${secure}`;
  document.cookie = `${REFERRAL_COOKIE_NAME}=; path=/; max-age=0; samesite=lax${secure}`;
}

function hasReferralStoragePermission(): boolean {
  const value = getCookie(REFERRAL_STORAGE_PERMISSION_COOKIE_NAME);
  return (
    value === 'allowed' ||
    value === 'automatic' ||
    /^v1:allowed:\d+$/.test(value ?? '')
  );
}

/**
 * Preserve first-touch attribution from the marketing-site cookie. Direct
 * dashboard referral parameters are accepted only when the website has
 * already established that referral storage is allowed.
 */
export function captureReferralCode(): void {
  const params = new URLSearchParams(window.location.search);
  const hasReferralParameter = params.has('ref');
  const refCode = normalizeReferralCode(params.get('ref'));

  if (hasReferralParameter) {
    // Clean the URL without reloading, whether or not storage is allowed.
    params.delete('ref');
    const newUrl =
      params.toString().length > 0
        ? `${window.location.pathname}?${params.toString()}${window.location.hash}`
        : `${window.location.pathname}${window.location.hash}`;
    window.history.replaceState({}, '', newUrl);
  }

  if (!hasReferralStoragePermission()) {
    // Do not access referral attribution before the website has established
    // that storage is allowed for this visitor.
    return;
  }

  if (!getReferralCookie() && refCode) setReferralCookie(refCode);
}

/**
 * Get the stored referral code, if any.
 */
export function getStoredReferralCode(): string | null {
  if (!hasReferralStoragePermission()) return null;
  return getReferralCookie();
}

/**
 * Clear first-touch attribution only after a successful authenticated claim.
 * The storage preference remains so consent or refusal is remembered and can
 * still be managed from the marketing-site footer.
 */
export function clearStoredReferralCode(): void {
  clearReferralCookie();
}
